import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PlusIcon, PencilSimpleIcon, EyeIcon, XIcon, AirplaneTiltIcon, ReceiptIcon, PaperclipIcon,
  ArrowRightIcon, PrinterIcon, FileXlsIcon, ChartBarIcon, UploadSimpleIcon, LinkIcon, TrashIcon, CheckCircleIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const ESTADOS = {
  borrador: { bg: "bg-text-muted/20", text: "text-text-muted", label: "Borrador" },
  en_transito: { bg: "bg-agro-yellow/20", text: "text-agro-yellow", label: "En tránsito" },
  en_aduana: { bg: "bg-orange-500/20", text: "text-orange-400", label: "En aduana" },
  internado: { bg: "bg-agro-green/20", text: "text-agro-green", label: "Internado" },
  arribado: { bg: "bg-blue-500/20", text: "text-blue-400", label: "Arribado" },          // legado
  costeado: { bg: "bg-agro-green/20", text: "text-agro-green", label: "Costeado" },       // legado
  anulada: { bg: "bg-red-500/20", text: "text-red-400", label: "Anulada" },
};
const CERRADOS = ["internado", "costeado", "anulada"];
// Siguiente paso del flujo (el internado se hace costeando)
const SIGUIENTE = { borrador: "en_transito", en_transito: "en_aduana", arribado: "en_aduana" };

const TIPOS_COSTO = [
  { v: "flete", l: "Flete internacional" },
  { v: "dai", l: "DAI (Derecho aduana)" },
  { v: "isv", l: "ISV importación (crédito fiscal)" },
  { v: "agente_aduanero", l: "Agente aduanero" },
  { v: "seguro", l: "Seguro de carga" },
  { v: "almacenaje", l: "Almacenaje" },
  { v: "transporte_interno", l: "Transporte interno" },
  { v: "otro", l: "Otro cargo" },
];

const METODOS_PRORRATEO = [
  { v: "valor", l: "Por valor (FOB L.)" },
  { v: "peso", l: "Por peso (kg)" },
  { v: "cantidad", l: "Por cantidad (uds.)" },
  { v: "configurable", l: "Configurable (% manual)" },
];

const DOC_TIPOS = [
  { v: "pre_manifiesto", l: "Pre-manifiesto" },
  { v: "poliza", l: "Póliza de importación" },
  { v: "factura_proveedor", l: "Factura del proveedor" },
  { v: "conocimiento_embarque", l: "Conocimiento de embarque / Guía aérea" },
  { v: "seguro_carga", l: "Seguro de carga" },
];

// Defaults sugeridos por tipo de costo
const DEFAULT_METODO = {
  flete: "valor", seguro: "valor", dai: "valor", isv: "valor",
  agente_aduanero: "valor", almacenaje: "valor", transporte_interno: "peso", otro: "valor",
};

const empty = {
  codigo: "", fecha_pedido: new Date().toISOString().slice(0, 10), fecha_arribo: "",
  proveedor_id: "", proveedor_nombre: "", numero_factura_proveedor: "", pais_origen: "", moneda: "USD", tipo_cambio: 24.50,
  items: [], costos: [], estado: "borrador", notas: "",
};

export default function Imports() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [docsFor, setDocsFor] = useState(null);
  const [showReport, setShowReport] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [imps, prods, sups, whs] = await Promise.all([
        api.get("/imports"), api.get("/products"), api.get("/suppliers"), api.get("/warehouses"),
      ]);
      setItems(imps.data); setProducts(prods.data); setSuppliers(sups.data); setWarehouses(whs.data);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const totalFobL = (form.items || []).reduce((s, it) => s + (Number(it.cantidad) * Number(it.costo_fob_unit) * Number(form.tipo_cambio)), 0);
  const totalCostos = (form.costos || []).filter((c) => c.tipo !== "isv").reduce((s, c) => s + Number(c.monto_l || 0), 0);
  const totalIsv = (form.costos || []).filter((c) => c.tipo === "isv").reduce((s, c) => s + Number(c.monto_l || 0), 0);
  const factor = totalFobL > 0 ? totalCostos / totalFobL : 0;
  const usaConfigurable = (form.costos || []).some((c) => c.metodo_prorrateo === "configurable" && c.tipo !== "isv");
  const sumaPct = (form.items || []).reduce((s, it) => s + Number(it.prorrateo_pct || 0), 0);

  const addItem = () => setForm({...form, items: [...(form.items || []), { producto_id: "", producto_codigo: "", descripcion: "", cantidad: 0, peso_kg: 0, costo_fob_unit: 0, prorrateo_pct: 0, bodega_id: "", bodega_codigo: "", ubicacion: "", lote: "", lote_fabricacion: "", fecha_vencimiento: "" }]});
  const updateItem = (i, patch) => {
    const list = [...form.items];
    list[i] = { ...list[i], ...patch };
    if (patch.producto_id) {
      const p = products.find((x) => x.id === patch.producto_id);
      if (p) { list[i].producto_codigo = p.codigo; list[i].descripcion = p.nombre; }
    }
    if (patch.bodega_id) {
      const w = warehouses.find((x) => x.id === patch.bodega_id);
      if (w) list[i].bodega_codigo = w.codigo;
    }
    setForm({...form, items: list});
  };
  const removeItem = (i) => setForm({...form, items: form.items.filter((_, x) => x !== i)});

  const addCosto = () => setForm({...form, costos: [...(form.costos || []), { tipo: "flete", descripcion: "", monto_l: 0, metodo_prorrateo: "valor", proveedor_id: "", numero_factura: "" }]});
  const updateCosto = (i, patch) => {
    const c = [...form.costos];
    c[i] = {...c[i], ...patch};
    // Si cambia el tipo, sugerir el método default
    if (patch.tipo && !patch.metodo_prorrateo) c[i].metodo_prorrateo = DEFAULT_METODO[patch.tipo] || "valor";
    setForm({...form, costos: c});
  };
  const removeCosto = (i) => setForm({...form, costos: form.costos.filter((_, x) => x !== i)});

  const onSave = async (e) => {
    e.preventDefault();
    try {
      if (!form.codigo || !form.items.length) { toast.error("Código y al menos 1 producto requeridos"); return; }
      if (usaConfigurable && Math.abs(sumaPct - 100) > 0.01) { toast.error(`Prorrateo configurable: los % por ítem deben sumar 100 (suman ${sumaPct.toFixed(2)})`); return; }
      const sup = suppliers.find((s) => s.id === form.proveedor_id);
      const payload = {
        ...form, tipo_cambio: Number(form.tipo_cambio),
        proveedor_nombre: sup ? sup.nombre : form.proveedor_nombre,
        items: form.items.map((it) => ({...it, cantidad: Number(it.cantidad), costo_fob_unit: Number(it.costo_fob_unit),
          peso_kg: Number(it.peso_kg || 0), prorrateo_pct: Number(it.prorrateo_pct || 0), fecha_vencimiento: it.fecha_vencimiento || null})),
        costos: form.costos.map((c) => ({...c, monto_l: Number(c.monto_l),
          proveedor_nombre: suppliers.find((s) => s.id === c.proveedor_id)?.nombre || ""})),
      };
      if (editing) { await api.put(`/imports/${editing.id}`, payload); toast.success("Importación actualizada"); }
      else { await api.post("/imports", payload); toast.success("Importación registrada"); }
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const advance = async (it, estado) => {
    try {
      await api.post(`/imports/${it.id}/estado`, { estado });
      toast.success(`${it.codigo}: ${ESTADOS[estado]?.label || estado}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const cost = async (id) => {
    if (!window.confirm("Costear e internar: prorratea costos, ingresa el inventario (PEPS/kardex), genera CxP y el asiento contable. ¿Continuar?")) return;
    try {
      const { data } = await api.post(`/imports/${id}/cost`);
      toast.success(`Internada. Factor internación: ${(data.factor_internacion * 100).toFixed(2)}%`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const im = useModalForm({ value: form, abierto: open, onClose: () => setOpen(false), onSave: onSave });
  return (
    <Layout title="Importaciones" subtitle={`${items.length} importaciones · En tránsito → En aduana → Internado · Costo de internación prorrateado a PEPS`}
      actions={
        <div className="flex gap-2">
          <button onClick={() => setShowReport(true)} data-testid="imports-report-btn"
            className="border border-border hover:border-agro-yellow/40 text-text-secondary hover:text-text-primary rounded-md px-3 py-2 text-sm flex items-center gap-2">
            <ChartBarIcon size={16} /> Reporte de internación
          </button>
          <button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="new-import-btn"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 transition-all active:scale-95">
            <PlusIcon size={16} weight="bold" /> Nueva importación
          </button>
        </div>
      }
    >
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Código</th>
              <th className="p-3">Proveedor</th>
              <th className="p-3">Origen</th>
              <th className="p-3">F. Pedido</th>
              <th className="p-3 text-right">FOB (L.)</th>
              <th className="p-3 text-right">Costos internación</th>
              <th className="p-3 text-right">Factor</th>
              <th className="p-3">Estado</th>
              <th className="p-3 text-right pr-5">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="9" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : items.length === 0 ? <tr><td colSpan="9" className="p-8 text-center text-text-muted">Sin importaciones registradas.</td></tr>
              : items.map((it) => {
                const est = ESTADOS[it.estado] || ESTADOS.borrador;
                const sig = SIGUIENTE[it.estado];
                const puedeCostear = it.estado === "en_aduana" || it.estado === "arribado";
                return (
                  <tr key={it.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`import-row-${it.codigo}`}>
                    <td className="p-3 pl-5 font-mono text-xs text-agro-green">{it.codigo}</td>
                    <td className="p-3 text-sm">{it.proveedor_nombre || "—"}</td>
                    <td className="p-3 text-xs text-text-secondary">{it.pais_origen || "—"}</td>
                    <td className="p-3 text-xs text-text-secondary">{fmtDate(it.fecha_pedido)}</td>
                    <td className="p-3 text-right text-sm">{fmtL(it.total_fob_l)}</td>
                    <td className="p-3 text-right text-sm">{fmtL(it.total_costos_internacion_l)}</td>
                    <td className="p-3 text-right text-xs">{((it.factor_internacion || 0) * 100).toFixed(2)}%</td>
                    <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded font-medium ${est.bg} ${est.text}`}>{est.label}</span></td>
                    <td className="p-3 pr-5 text-right flex justify-end gap-1">
                      <button onClick={() => setViewing(it)} title="Ver detalle / reporte de costo" className="p-1.5 text-text-muted hover:text-agro-yellow" data-testid={`view-import-${it.codigo}`}><EyeIcon size={16} /></button>
                      <button onClick={() => setDocsFor(it)} title="Documentos" className="p-1.5 text-text-muted hover:text-agro-yellow" data-testid={`docs-import-${it.codigo}`}><PaperclipIcon size={16} /></button>
                      {!CERRADOS.includes(it.estado) && (
                        <>
                          <button aria-label="Editar" onClick={() => { setEditing(it); setForm({...empty, ...it, fecha_pedido: (it.fecha_pedido || "").slice(0,10), fecha_arribo: (it.fecha_arribo || "").slice(0,10)}); setOpen(true); }} className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`edit-import-${it.codigo}`}><PencilSimpleIcon size={16} /></button>
                          {sig && (
                            <button onClick={() => advance(it, sig)} title={`Pasar a ${ESTADOS[sig].label}`} className="px-2 py-1 text-[10px] border border-border rounded text-text-secondary hover:text-agro-yellow flex items-center gap-1" data-testid={`advance-import-${it.codigo}`}>
                              <ArrowRightIcon size={10} /> {ESTADOS[sig].label}
                            </button>
                          )}
                          {puedeCostear && (
                            <button onClick={() => cost(it.id)} title="Costear e internar al inventario" className="px-2 py-1 text-[10px] border border-agro-green/40 rounded text-agro-green hover:bg-agro-green/10 flex items-center gap-1" data-testid={`cost-import-${it.codigo}`}>
                              <ReceiptIcon size={12} /> Internar
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...im.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="import-modal">
            <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <AirplaneTiltIcon size={22} className="text-agro-green" /> {editing ? "Editar importación" : "Nueva importación"}
              </h3>
              <button aria-label="Cerrar" onClick={im.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <form onSubmit={im.guardar} className="p-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <F label="Código *" v={form.codigo} on={(v) => setForm({...form, codigo: v})} testid="imp-codigo" required />
                <F label="Fecha pedido" type="date" v={form.fecha_pedido} on={(v) => setForm({...form, fecha_pedido: v})} />
                <F label="Fecha arribo (esperada)" type="date" v={form.fecha_arribo} on={(v) => setForm({...form, fecha_arribo: v})} />
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Proveedor del exterior</label>
                  <select value={form.proveedor_id} onChange={(e) => { const s = suppliers.find((x) => x.id === e.target.value); setForm({...form, proveedor_id: e.target.value, proveedor_nombre: s ? s.nombre : ""}); }} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" data-testid="imp-proveedor">
                    <option value="">— Seleccionar —</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.codigo} · {s.nombre}</option>)}
                  </select>
                </div>
                <F label="N° factura proveedor" v={form.numero_factura_proveedor} on={(v) => setForm({...form, numero_factura_proveedor: v})} testid="imp-num-factura" />
                <F label="País origen" v={form.pais_origen} on={(v) => setForm({...form, pais_origen: v})} />
                <F label="Moneda" v={form.moneda} on={(v) => setForm({...form, moneda: v})} />
                <F label="Tipo de cambio (L./moneda)" type="number" v={form.tipo_cambio} on={(v) => setForm({...form, tipo_cambio: v})} />
              </div>
              {!editing && (
                <div className="text-[11px] text-text-muted">El estado avanza desde la lista: Borrador → En tránsito → En aduana → <b>Internar</b> (costea e ingresa a inventario).</div>
              )}

              {/* Items */}
              <div className="border-t border-border/60 pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-heading text-sm uppercase tracking-wider text-agro-green">Productos a importar</h4>
                  <button type="button" onClick={addItem} className="text-xs px-3 py-1.5 border border-agro-green/30 text-agro-green rounded-md hover:bg-agro-green/10" data-testid="imp-add-item">+ Agregar producto</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end bg-app rounded p-2 border border-border/40">
                      <div className="col-span-3">
                        <label className="text-[9px] uppercase text-text-muted">Producto</label>
                        <select value={it.producto_id} onChange={(e) => updateItem(i, {producto_id: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs">
                          <option value="">—</option>
                          {products.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                        </select>
                      </div>
                      <div className="col-span-1"><label className="text-[9px] uppercase text-text-muted">Cant.</label><input type="number" value={it.cantidad} onChange={(e) => updateItem(i, {cantidad: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" /></div>
                      <div className="col-span-1"><label className="text-[9px] uppercase text-text-muted">Peso kg</label><input type="number" step="0.01" value={it.peso_kg ?? 0} onChange={(e) => updateItem(i, {peso_kg: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" /></div>
                      <div className="col-span-1"><label className="text-[9px] uppercase text-text-muted">FOB unit ({form.moneda})</label><input type="number" step="0.0001" value={it.costo_fob_unit} onChange={(e) => updateItem(i, {costo_fob_unit: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" /></div>
                      <div className="col-span-1 text-right text-[10px] text-text-secondary">FOB L.<br/><span className="text-text-primary">{fmtL(Number(it.cantidad) * Number(it.costo_fob_unit) * Number(form.tipo_cambio))}</span></div>
                      <div className="col-span-1">
                        <label className="text-[9px] uppercase text-text-muted" title="Solo para costos con prorrateo configurable">% manual</label>
                        <input type="number" step="0.01" value={it.prorrateo_pct ?? 0} disabled={!usaConfigurable}
                          onChange={(e) => updateItem(i, {prorrateo_pct: e.target.value})} data-testid={`imp-pct-${i}`}
                          className="w-full bg-card border border-border rounded px-2 py-1 text-xs disabled:opacity-30" />
                      </div>
                      <div className="col-span-1">
                        <label className="text-[9px] uppercase text-text-muted">Bodega</label>
                        <select value={it.bodega_id || ""} onChange={(e) => updateItem(i, {bodega_id: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs">
                          <option value="">—</option>
                          {warehouses.filter((w) => w.tipo !== "transito").map((w) => <option key={w.id} value={w.id}>{w.codigo}</option>)}
                        </select>
                      </div>
                      <div className="col-span-1"><label className="text-[9px] uppercase text-text-muted">Ubicación</label><input value={it.ubicacion || ""} onChange={(e) => updateItem(i, {ubicacion: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" /></div>
                      <div className="col-span-1"><label className="text-[9px] uppercase text-text-muted">Batch</label><input value={it.lote || ""} onChange={(e) => updateItem(i, {lote: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" /></div>
                      <div className="col-span-1"><label className="text-[9px] uppercase text-text-muted" title="Lote de fabricación impreso por el proveedor">Lote fab.</label><input value={it.lote_fabricacion || ""} onChange={(e) => updateItem(i, {lote_fabricacion: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" /></div>
                      <div className="col-span-1"><label className="text-[9px] uppercase text-text-muted">Vence</label><input type="date" value={(it.fecha_vencimiento || "").slice(0, 10)} onChange={(e) => updateItem(i, {fecha_vencimiento: e.target.value})} className="w-full bg-card border border-border rounded px-1 py-1 text-[10px]" /></div>
                      <div className="col-span-12 flex justify-end -mt-1"><button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-300 text-[10px] inline-flex items-center gap-0.5"><XIcon size={11} aria-hidden="true" /> Quitar</button></div>
                    </div>
                  ))}
                  {form.items.length === 0 && <div className="text-center text-text-muted text-xs py-3">Sin productos — agregue al menos uno.</div>}
                  {usaConfigurable && (
                    <div className={`text-xs ${Math.abs(sumaPct - 100) > 0.01 ? "text-red-400" : "text-agro-green"}`} data-testid="imp-pct-sum">
                      Prorrateo configurable: Σ % por ítem = {sumaPct.toFixed(2)}% (debe ser 100%)
                    </div>
                  )}
                </div>
              </div>

              {/* Costos */}
              <div className="border-t border-border/60 pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-heading text-sm uppercase tracking-wider text-agro-yellow">Costos de internación (en lempiras)</h4>
                  <button type="button" onClick={addCosto} className="text-xs px-3 py-1.5 border border-agro-yellow/30 text-agro-yellow rounded-md hover:bg-agro-yellow/10" data-testid="imp-add-costo">+ Agregar costo</button>
                </div>
                <div className="space-y-2">
                  {form.costos.map((c, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end bg-app rounded p-2 border border-border/40">
                      <div className="col-span-2"><label className="text-[9px] uppercase text-text-muted">Tipo</label>
                        <select value={c.tipo} onChange={(e) => updateCosto(i, {tipo: e.target.value, metodo_prorrateo: DEFAULT_METODO[e.target.value] || "valor"})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs">
                          {TIPOS_COSTO.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                        </select></div>
                      <div className="col-span-2"><label className="text-[9px] uppercase text-text-muted">Descripción</label><input value={c.descripcion} onChange={(e) => updateCosto(i, {descripcion: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" /></div>
                      <div className="col-span-2"><label className="text-[9px] uppercase text-text-muted">Monto (L.)</label><input type="number" value={c.monto_l} onChange={(e) => updateCosto(i, {monto_l: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" /></div>
                      <div className="col-span-2"><label className="text-[9px] uppercase text-text-muted">Prorratear por</label>
                        <select value={c.metodo_prorrateo || "valor"} disabled={c.tipo === "isv"} onChange={(e) => updateCosto(i, {metodo_prorrateo: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs disabled:opacity-40" data-testid={`prorrateo-${i}`}>
                          {METODOS_PRORRATEO.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                        </select></div>
                      <div className="col-span-2"><label className="text-[9px] uppercase text-text-muted" title="Opcional: genera su factura en CxP">Proveedor (agente, naviera)</label>
                        <select value={c.proveedor_id || ""} onChange={(e) => updateCosto(i, {proveedor_id: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" data-testid={`costo-proveedor-${i}`}>
                          <option value="">— CxP genérica —</option>
                          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                        </select></div>
                      <div className="col-span-1"><label className="text-[9px] uppercase text-text-muted">N° factura</label><input value={c.numero_factura || ""} onChange={(e) => updateCosto(i, {numero_factura: e.target.value})} className="w-full bg-card border border-border rounded px-2 py-1 text-xs" /></div>
                      <div className="col-span-1 flex justify-end"><button type="button" onClick={() => removeCosto(i)} className="text-red-400 hover:text-red-300 text-xs p-1.5" aria-label="Quitar costo"><XIcon size={14} /></button></div>
                    </div>
                  ))}
                  {form.costos.length === 0 && <div className="text-center text-text-muted text-xs py-3">Sin costos — agregue flete, DAI, ISV, etc.</div>}
                </div>
              </div>

              {/* Totales */}
              <div className="bg-app border border-border rounded p-3 grid grid-cols-4 gap-4 text-sm">
                <div><div className="text-[10px] uppercase text-text-muted">Total FOB</div><div className="text-text-primary font-semibold">{fmtL(totalFobL)}</div></div>
                <div><div className="text-[10px] uppercase text-text-muted">Costos internación (al costo)</div><div className="text-text-primary font-semibold">{fmtL(totalCostos)}</div></div>
                <div><div className="text-[10px] uppercase text-text-muted">ISV importación (crédito fiscal)</div><div className="text-text-secondary font-semibold">{fmtL(totalIsv)}</div></div>
                <div><div className="text-[10px] uppercase text-text-muted">Factor internación</div><div className="text-agro-green font-semibold">{(factor * 100).toFixed(2)}%</div></div>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={im.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={im.saving} type="submit" data-testid="import-save-btn" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewing && <CostReportModal imp={viewing} onClose={() => setViewing(null)} />}
      {docsFor && <ImportDocsModal imp={docsFor} onClose={() => setDocsFor(null)} />}
      {showReport && <AllImportsReport onClose={() => setShowReport(false)} />}
    </Layout>
  );
}

async function downloadBlob(url, params, filename) {
  try {
    const res = await api.get(url, { params, responseType: "blob" });
    const href = window.URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = href; a.download = filename; a.click();
    window.URL.revokeObjectURL(href);
  } catch (e) { toast.error(apiErr(e)); }
}

const tipoLabel = (t) => TIPOS_COSTO.find((x) => x.v === t)?.l || t;

function CostReportModal({ imp, onClose }) {
  const [rep, setRep] = useState(null);
  useEffect(() => {
    api.get(`/imports/${imp.id}/cost-report`).then(({ data }) => setRep(data)).catch((e) => toast.error(apiErr(e)));
  }, [imp.id]);
  const h = rep?.import || imp;
  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="import-view-modal" id="import-cost-print">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="font-heading text-lg font-semibold">Costo de internación · {h.codigo}</div>
            <div className="text-xs text-text-muted">{h.proveedor_nombre} · {h.pais_origen} · {h.moneda} × TC {h.tipo_cambio} · {ESTADOS[h.estado]?.label || h.estado}</div>
          </div>
          <div className="flex gap-2 print:hidden">
            <button onClick={() => window.print()} className="px-3 py-1.5 text-xs border border-border rounded-md text-text-secondary flex items-center gap-1"><PrinterIcon size={14} /> Imprimir</button>
            <button onClick={() => downloadBlob(`/imports/${imp.id}/cost-report`, { formato: "xlsx" }, `internacion_${h.codigo}.xlsx`)} className="px-3 py-1.5 text-xs border border-border rounded-md text-text-secondary flex items-center gap-1" data-testid="import-report-xlsx"><FileXlsIcon size={14} /> Excel</button>
            <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-5 gap-3 text-sm">
            <div><div className="text-[10px] uppercase text-text-muted">FOB Total</div>{fmtL(h.total_fob_l)}</div>
            <div><div className="text-[10px] uppercase text-text-muted">Costos Intern.</div>{fmtL(h.total_costos_internacion_l)}</div>
            <div><div className="text-[10px] uppercase text-text-muted">ISV (crédito fiscal)</div>{fmtL(h.total_isv_importacion_l)}</div>
            <div><div className="text-[10px] uppercase text-text-muted">Total Costeado</div><span className="text-agro-green">{fmtL(h.total_costeado_l)}</span></div>
            <div><div className="text-[10px] uppercase text-text-muted">Factor</div>{((h.factor_internacion || 0) * 100).toFixed(2)}%</div>
          </div>
          {!rep ? <div className="text-center text-text-muted text-sm py-6">Cargando…</div> : (
            <>
              <table className="w-full text-xs" data-testid="import-report-items">
                <thead className="bg-app">
                  <tr className="text-left text-[10px] uppercase text-text-muted">
                    <th className="p-2">Producto</th><th className="p-2 text-right">Cant.</th><th className="p-2 text-right">FOB L.</th>
                    {rep.tipos_costo.map((t) => <th key={t} className="p-2 text-right">{tipoLabel(t)}</th>)}
                    <th className="p-2 text-right">Internación</th><th className="p-2 text-right">Costo total</th><th className="p-2 text-right">Costo unit.</th>
                  </tr>
                </thead>
                <tbody>
                  {rep.items.map((it, i) => (
                    <tr key={i} className="border-t border-border/40">
                      <td className="p-2 font-mono">{it.producto_codigo} · {it.descripcion}</td>
                      <td className="p-2 text-right">{it.cantidad}</td>
                      <td className="p-2 text-right">{fmtL(it.fob_l)}</td>
                      {rep.tipos_costo.map((t) => <td key={t} className="p-2 text-right">{fmtL(it.desglose[t] || 0)}</td>)}
                      <td className="p-2 text-right">{fmtL(it.internacion_total)}</td>
                      <td className="p-2 text-right">{fmtL(it.costo_total_final)}</td>
                      <td className="p-2 text-right text-agro-green">{fmtL(it.costo_unitario_final)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <table className="w-full text-xs">
                <thead className="bg-app">
                  <tr className="text-left text-[10px] uppercase text-text-muted"><th className="p-2">Tipo</th><th className="p-2">Descripción</th><th className="p-2">Prorrateo</th><th className="p-2">Proveedor</th><th className="p-2 text-right">Monto</th></tr>
                </thead>
                <tbody>
                  {rep.costos.map((c, i) => (
                    <tr key={i} className="border-t border-border/40">
                      <td className="p-2">{tipoLabel(c.tipo)}{!c.al_costo && <span className="ml-1 text-[10px] text-agro-yellow">(no va al costo)</span>}</td>
                      <td className="p-2">{c.descripcion}</td><td className="p-2">{c.al_costo ? c.metodo_prorrateo : "—"}</td>
                      <td className="p-2">{c.proveedor || "—"}</td><td className="p-2 text-right">{fmtL(c.monto_l)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
      <style>{`@media print { body * { visibility: hidden; } #import-cost-print, #import-cost-print * { visibility: visible; } #import-cost-print { position: absolute; left: 0; top: 0; max-height: none; overflow: visible; background: white; color: black; } }`}</style>
    </div>
  );
}

function AllImportsReport({ onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/imports/cost-report").then(({ data: d }) => setData(d)).catch((e) => toast.error(apiErr(e)));
  }, []);
  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="imports-report-modal" id="imports-all-print">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div className="font-heading text-lg font-semibold">Reporte de costo de internación por importación</div>
          <div className="flex gap-2 print:hidden">
            <button onClick={() => window.print()} className="px-3 py-1.5 text-xs border border-border rounded-md text-text-secondary flex items-center gap-1"><PrinterIcon size={14} /> Imprimir</button>
            <button onClick={() => downloadBlob("/imports/cost-report", { formato: "xlsx" }, "costo_internacion.xlsx")} className="px-3 py-1.5 text-xs border border-border rounded-md text-text-secondary flex items-center gap-1"><FileXlsIcon size={14} /> Excel</button>
            <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
          </div>
        </div>
        <div className="p-5">
          {!data ? <div className="text-center text-text-muted py-6">Cargando…</div> : (
            <table className="w-full text-xs">
              <thead className="bg-app">
                <tr className="text-left text-[10px] uppercase text-text-muted">
                  <th className="p-2">Código</th><th className="p-2">Proveedor</th><th className="p-2">Internada</th>
                  <th className="p-2 text-right">FOB L.</th><th className="p-2 text-right">Internación L.</th><th className="p-2 text-right">ISV (crédito)</th>
                  <th className="p-2 text-right">Total costeado</th><th className="p-2 text-right">Factor</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? <tr><td colSpan="8" className="p-6 text-center text-text-muted">Sin importaciones internadas</td></tr> : data.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border/40">
                    <td className="p-2 font-mono text-agro-green">{r.codigo}</td><td className="p-2">{r.proveedor}</td><td className="p-2">{fmtDate(r.costeado_at)}</td>
                    <td className="p-2 text-right">{fmtL(r.total_fob_l)}</td><td className="p-2 text-right">{fmtL(r.total_costos_internacion_l)}</td>
                    <td className="p-2 text-right">{fmtL(r.total_isv_importacion_l)}</td><td className="p-2 text-right">{fmtL(r.total_costeado_l)}</td>
                    <td className="p-2 text-right">{((r.factor_internacion || 0) * 100).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-app font-semibold">
                <tr><td className="p-2" colSpan="3">TOTAL</td><td className="p-2 text-right">{fmtL(data.totales.total_fob_l)}</td>
                  <td className="p-2 text-right">{fmtL(data.totales.total_costos_internacion_l)}</td><td className="p-2"></td>
                  <td className="p-2 text-right">{fmtL(data.totales.total_costeado_l)}</td><td className="p-2"></td></tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
      <style>{`@media print { body * { visibility: hidden; } #imports-all-print, #imports-all-print * { visibility: visible; } #imports-all-print { position: absolute; left: 0; top: 0; max-height: none; overflow: visible; background: white; color: black; } }`}</style>
    </div>
  );
}

function ImportDocsModal({ imp, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const [pendingTipo, setPendingTipo] = useState("");

  const load = async () => {
    try { const { data: d } = await api.get(`/imports/${imp.id}/documents`); setData(d); }
    catch (e) { toast.error(apiErr(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [imp.id]);

  const attach = async (tipo, url, nombre, tamano = 0) => {
    await api.post(`/attachments/import/${imp.id}`, { url, nombre, tipo, tamano_bytes: tamano, descripcion: DOC_TIPOS.find((d) => d.v === tipo)?.l || "" });
  };
  const pickFile = (tipo) => { setPendingTipo(tipo); fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingTipo) return;
    setBusy(pendingTipo);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("folder", "documents"); fd.append("kind", "any");
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await attach(pendingTipo, up.url, file.name, file.size);
      toast.success("Documento adjuntado");
      load();
    } catch (er) { toast.error(apiErr(er)); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const addLink = async (tipo) => {
    const url = window.prompt("Pega el enlace del documento (https://…):");
    if (!url) return;
    try { await attach(tipo, url, url.split("/").pop()); toast.success("Enlace agregado"); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const remove = async (att) => {
    if (!window.confirm(`¿Eliminar "${att.nombre}"?`)) return;
    try { await api.delete(`/attachments/import/${imp.id}/${att.id}`); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };

  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="import-docs-modal">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="font-heading text-lg font-semibold flex items-center gap-2"><PaperclipIcon size={18} className="text-agro-yellow" /> Documentos · {imp.codigo}</div>
            {data && <div className="text-xs text-text-muted">{data.completos} de {data.total_tipos} documentos requeridos</div>}
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="import-doc-file" />
        <div className="p-5 space-y-3">
          {!data ? <div className="text-center text-text-muted py-6">Cargando…</div> : data.checklist.map((c) => (
            <div key={c.tipo} className="border border-border/60 rounded-md p-3" data-testid={`import-doc-${c.tipo}`}>
              <div className="flex items-center gap-2">
                <CheckCircleIcon size={16} weight={c.completo ? "fill" : "regular"} className={c.completo ? "text-agro-green" : "text-text-muted"} />
                <div className="text-sm font-medium flex-1">{c.label}</div>
                <button onClick={() => pickFile(c.tipo)} disabled={!!busy} className="text-xs px-2.5 py-1 border border-agro-green/40 text-agro-green rounded flex items-center gap-1 disabled:opacity-50">
                  <UploadSimpleIcon size={12} /> {busy === c.tipo ? "Subiendo…" : "Subir"}
                </button>
                <button onClick={() => addLink(c.tipo)} className="text-xs px-2.5 py-1 border border-border text-text-secondary rounded flex items-center gap-1">
                  <LinkIcon size={12} /> Enlace
                </button>
              </div>
              {c.documentos.length > 0 && (
                <div className="mt-2 space-y-1">
                  {c.documentos.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 text-xs pl-6">
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate hover:text-agro-green">{d.nombre || d.url}</a>
                      <span className="text-text-muted">{fmtDate(d.created_at)}</span>
                      <button aria-label="Eliminar" onClick={() => remove(d)} className="text-text-muted hover:text-red-400"><TrashIcon size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function F({ label, v, on, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <input data-testid={testid} type={type} value={v ?? ""} required={required} onChange={(e) => on(e.target.value)}
        className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
    </div>
  );
}
